import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import BarcodeScanner from "../../components/marketInventory/BarcodeScanner";
import StockAdjustmentModal from "../../components/marketInventory/StockAdjustmentModal";
import {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  scanItemInit,
  scanItemSuccess,
  scanItemFailure,
  clearScanResult,
  setScanMode,
  adjustStockSuccess,
  adjustStockFailure
} from "../../store/marketInventory/actions";
import * as API from "../../store/marketInventory/api";
import * as InventoryAPI from "../../store/inventories/api";
import { isStaffUser, isSuperUser } from "../../utils/AuthService";

const BarcodeScannerContainer = ({
  scanning,
  locations,
  stockAdjustment,
  fetchLocationsSuccess,
  fetchLocationsFailure,
  scanItemInit,
  scanItemSuccess,
  scanItemFailure,
  clearScanResult,
  setScanMode,
  adjustStockSuccess,
  adjustStockFailure
}) => {
  const isAdmin = isStaffUser() || isSuperUser();
  const [selectedLocationId, setSelectedLocationId] = useState(isAdmin ? null : "auto");
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState(null);
  const [availableQuantity, setAvailableQuantity] = useState(null);
  const [pendingFifoScan, setPendingFifoScan] = useState(null);

  // Fetch locations on mount (admin only - market users don't need to pick)
  useEffect(() => {
    if (!isAdmin) return;
    const loadLocations = async () => {
      try {
        const response = await API.fetchLocations();
        fetchLocationsSuccess(response.data);
      } catch (error) {
        console.error("Error fetching locations:", error);
        fetchLocationsFailure(error.response?.data?.message || "Failed to fetch locations");
      }
    };
    loadLocations();
  }, [isAdmin, fetchLocationsSuccess, fetchLocationsFailure]);

  // Build scan payload — only include location_id for admin users
  const buildScanPayload = (barcode, locationId, extras = {}) => {
    const payload = { barcode, ...extras };
    if (isAdmin && locationId) {
      payload.location_id = locationId;
    }
    return payload;
  };

  const handleScanError = (error) => {
    console.error("Scan error:", error);
    if (error.response?.data) {
      const errorData = error.response.data;
      const msg = errorData.error || errorData.message || errorData.fifo_check?.message;

      // 409 + fifo_blocked → store blocked response for FIFO warning modal
      if (error.response.status === 409 && errorData.fifo_blocked) {
        scanItemSuccess({ ...errorData, _fifoBlocked: true });
        return;
      }

      if (error.response.status === 403) {
        scanItemFailure(msg || "You do not have permission to scan items.");
      } else if (error.response.status === 400) {
        scanItemFailure(msg || "Invalid QR code. Expected an Inventory ID.");
      } else if (error.response.status === 404) {
        scanItemFailure(msg || "Item not found");
      } else if (error.response.status === 422) {
        scanItemFailure(msg || "Insufficient stock for requested quantity");
      } else {
        scanItemFailure(msg || "Error scanning item");
      }
    } else {
      scanItemFailure("Network error. Please check your connection.");
    }
  };

  // Phase 1: Lookup only — get stock info + unit_info without consuming
  const handleScan = async (barcode, locationId) => {
    scanItemInit();
    setPendingFifoScan(null);
    try {
      const payload = { barcode };
      if (isAdmin && locationId) {
        payload.location_id = locationId;
      }
      const response = await API.scanItem(payload);
      scanItemSuccess(response.data);
    } catch (error) {
      handleScanError(error);
    }
  };

  // Phase 2: Actual consumption with consumption_type
  const handleConsume = async (barcode, locationId, consumptionType, quantity, purpose, notes) => {
    scanItemInit();
    setPendingFifoScan(null);
    try {
      const extras = { consumption_type: consumptionType, purpose, notes };
      if (consumptionType === 'partial') {
        extras.quantity = quantity;
      }
      const response = await API.scanItem(
        buildScanPayload(barcode, locationId, extras)
      );
      scanItemSuccess(response.data);
    } catch (error) {
      if (error.response?.status === 409 && error.response.data?.fifo_blocked) {
        setPendingFifoScan({ barcode, locationId, consumptionType, quantity, purpose, notes });
      }
      handleScanError(error);
    }
  };

  // Override FIFO — re-send the original scan with force_consume: true
  const handleForceConsume = async () => {
    if (!pendingFifoScan) return;
    const { barcode, locationId, consumptionType, quantity, purpose, notes } = pendingFifoScan;
    scanItemInit();
    setPendingFifoScan(null);
    try {
      const extras = { consumption_type: consumptionType, purpose, notes, force_consume: true };
      if (consumptionType === 'partial') {
        extras.quantity = quantity;
      }
      const response = await API.scanItem(
        buildScanPayload(barcode, locationId, extras)
      );
      scanItemSuccess({ ...response.data, _fifoOverridden: true });
    } catch (error) {
      handleScanError(error);
    }
  };

  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId);
  };

  const clearError = () => {
    clearScanResult();
  };

  // Search items for adjustment modal
  const handleSearchItems = useCallback(async (searchTerm) => {
    setItemsLoading(true);
    try {
      const response = await InventoryAPI.fetchItems(searchTerm);
      setItems(response.data.results || response.data || []);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  // Handle stock adjustment submission
  const handleAdjustmentSubmit = async (data) => {
    setAdjustmentError(null);
    setAvailableQuantity(null);
    try {
      const response = await API.adjustStock(data);
      adjustStockSuccess(response.data);
      setShowAdjustmentModal(false);
      // Show success in scan result
      scanItemSuccess({
        success: true,
        message: response.data.message,
        item_name: response.data.item,
        remaining_quantity: response.data.remaining_quantity,
        quantity_reduced: response.data.quantity_reduced,
        market_stock_id: response.data.market_stock_id
      });
    } catch (error) {
      console.error("Error adjusting stock:", error);
      const errorData = error.response?.data;
      const errorMessage = errorData?.message ||
        errorData?.error ||
        "Failed to adjust stock";

      // Check for insufficient stock error and extract available_quantity
      if (errorData?.available_quantity !== undefined) {
        setAvailableQuantity(errorData.available_quantity);
      }

      setAdjustmentError(errorMessage);
      adjustStockFailure(errorMessage);
    }
  };

  const handleOpenAdjustmentModal = () => {
    setAdjustmentError(null);
    setAvailableQuantity(null);
    setShowAdjustmentModal(true);
  };

  const handleCloseAdjustmentModal = () => {
    setShowAdjustmentModal(false);
    setAdjustmentError(null);
    setAvailableQuantity(null);
    setItems([]);
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-qrcode mr-05"></i>
            Inventory Scanner
          </span>
        </div>
      </div>

      <BarcodeScanner
        onScan={handleScan}
        onConsume={handleConsume}
        onForceConsume={handleForceConsume}
        scanResult={scanning.lastScanResult}
        loading={scanning.loading}
        error={scanning.error}
        clearError={clearError}
        scanMode={scanning.scanMode}
        setScanMode={setScanMode}
        locations={locations.list}
        locationsLoading={locations.loading}
        selectedLocationId={selectedLocationId}
        onLocationChange={handleLocationChange}
        onOpenUntrackedModal={handleOpenAdjustmentModal}
        isAdmin={isAdmin}
      />

      <StockAdjustmentModal
        show={showAdjustmentModal}
        onHide={handleCloseAdjustmentModal}
        onSubmit={handleAdjustmentSubmit}
        locations={locations.list}
        items={items}
        itemsLoading={itemsLoading}
        selectedLocationId={selectedLocationId}
        loading={stockAdjustment?.loading || false}
        error={adjustmentError}
        availableQuantity={availableQuantity}
        onSearchItems={handleSearchItems}
        isAdmin={isAdmin}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  scanning: state.marketInventory.scanning,
  locations: state.marketInventory.locations,
  stockAdjustment: state.marketInventory.stockAdjustment
});

const mapDispatchToProps = {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  scanItemInit,
  scanItemSuccess,
  scanItemFailure,
  clearScanResult,
  setScanMode,
  adjustStockSuccess,
  adjustStockFailure
};

export default connect(mapStateToProps, mapDispatchToProps)(BarcodeScannerContainer);
